/**
 * Corporate VIPs at /app/vips. Sponsor only. Everyone in Trellis inherits this layer
 * (docs/2026-09-20-vips.md). Program and unit layers stay on Structure (track G).
 */
import { useState } from "preact/hooks";

export interface VipLine { name: string; address: string; kind: "customer" | "internal"; role?: string }

export interface VipsScreenProps {
  entries: VipLine[];
  onSave: (entries: VipLine[]) => Promise<void>;
  notice?: { text: string; error?: boolean } | undefined;
}

export function VipsScreen({ entries, onSave, notice }: VipsScreenProps) {
  return (
    <main class="main settings">
      <div class="page-head">
        <span class="label">Corporate · everyone in Trellis</span>
        <h1 class="h-lg">Who is a VIP for everyone</h1>
        <p class="lede">An ask from these people is never dropped as noise. Customer kind is program intake; internal kind ranks first on the person's list. Program and unit layers are edited on Structure.</p>
        {notice && <div class={`notice${notice.error ? " error" : ""}`}>{notice.text}</div>}
      </div>
      <section class="panel">
        <VipEditor entries={entries} kindDefault="internal" onSave={onSave} />
      </section>
    </main>
  );
}

/**
 * `internalOnly` hides the kind picker for a scope where a customer is held somewhere else.
 * On a contract that is the customer register, and the route refuses a customer here, so
 * offering the choice would be offering one the save then rejects.
 */
export function VipEditor({ entries, kindDefault, internalOnly, onSave }: { entries: VipLine[]; kindDefault: "customer" | "internal"; internalOnly?: boolean; onSave: (entries: VipLine[]) => Promise<void> }) {
  const blank = { name: "", address: "", kind: kindDefault, role: "" };
  const [rows, setRows] = useState<{ name: string; address: string; kind: "customer" | "internal"; role: string }[]>(
    entries.length ? entries.map((e) => ({ name: e.name, address: e.address, kind: e.kind, role: e.role ?? "" })) : [blank],
  );
  const [busy, setBusy] = useState(false);
  const set = (i: number, k: "name" | "address" | "kind" | "role", v: string) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: k === "kind" ? (v === "customer" ? "customer" : "internal") : v } : r)));
  const kept = rows.filter((r) => r.name.trim() && r.address.trim());
  const ok = kept.length === rows.filter((r) => r.name.trim() || r.address.trim()).length;
  const save = async () => {
    setBusy(true);
    try { await onSave(kept.map((r) => ({ name: r.name.trim(), address: r.address.trim(), kind: r.kind, ...(r.role.trim() ? { role: r.role.trim() } : {}) }))); }
    finally { setBusy(false); }
  };
  return (
    <div class="inline-form register-edit">
      {rows.map((r, i) => (
        <div class="fields" key={i}>
          <label class="sr-only" for={`vip-name-${i}`}>Name</label>
          <input id={`vip-name-${i}`} type="text" value={r.name} placeholder="Name" onInput={(e) => set(i, "name", (e.target as HTMLInputElement).value)} />
          <label class="sr-only" for={`vip-addr-${i}`}>Address or domain</label>
          <input id={`vip-addr-${i}`} type="text" value={r.address} placeholder="Address or @domain" onInput={(e) => set(i, "address", (e.target as HTMLInputElement).value)} />
          {!internalOnly && (
            <>
              <label class="sr-only" for={`vip-kind-${i}`}>Kind</label>
              <select id={`vip-kind-${i}`} value={r.kind} onChange={(e) => set(i, "kind", (e.target as HTMLSelectElement).value)}>
                <option value="internal">Internal</option>
                <option value="customer">Customer</option>
              </select>
            </>
          )}
          <label class="sr-only" for={`vip-role-${i}`}>Relation</label>
          <input id={`vip-role-${i}`} type="text" value={r.role} placeholder="Relation" onInput={(e) => set(i, "role", (e.target as HTMLInputElement).value)} />
          <button class="link" aria-label={`Remove ${r.name.trim() || `line ${i + 1}`}`} onClick={() => setRows(rows.filter((_, j) => j !== i))}>Remove</button>
        </div>
      ))}
      <div class="buttons">
        <button class="link" onClick={() => setRows([...rows, blank])}>Add a line</button>
        <button class="btn" disabled={busy || !ok} onClick={() => void save()}>Save</button>
      </div>
    </div>
  );
}
